import { useMemo, useState, useEffect } from "react";
import {
  closestCorners,
  DndContext,
  DragOverlay,
  MeasuringStrategy,
  PointerSensor,
  TouchSensor,
  useDroppable,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import { restrictToVerticalAxis } from "@dnd-kit/modifiers";
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { GripVertical, Check } from "lucide-react";
import { toast } from "sonner";
import { api, formatErr } from "../lib/api";
import { absoluteUrl } from "./ImageUpload";
import { useAuth } from "../context/AuthContext";

function Row({ id, data, children, className = "" }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
    isOver,
  } = useSortable({ id, data });

  return (
    <div
      ref={setNodeRef}
      style={{
        transform: CSS.Transform.toString(transform),
        transition: transition || "transform 160ms cubic-bezier(0.2, 0, 0, 1)",
      }}
      className={`${className} ${isDragging ? "opacity-30" : ""} ${
        isOver ? "menu-order-sortable-over" : ""
      } menu-order-sortable`}
    >
      {children({
        handleProps: {
          ...attributes,
          ...listeners,
          style: { touchAction: "none" },
        },
      })}
    </div>
  );
}

export default function MenuOrderEditor({ categories, products, onReload, onExit }) {
  const { user } = useAuth();
  const [cats, setCats] = useState(categories);
  const [prods, setProds] = useState(products);
  const [pending, setPending] = useState(null); // {type, payload, message}
  const [busy, setBusy] = useState(false);
  const [activeDrag, setActiveDrag] = useState(null);
  const [showSortHint, setShowSortHint] = useState(false);
  const isDraggingCategory = activeDrag?.type === "category";
  const isDraggingProduct = activeDrag?.type === "product";
  const sortHintStorageKey = user?.id ? `hasSeenSortHint:${user.id}` : null;
  // Moving/copying option groups hits /menu/option-groups/move (products_edit + addons_edit).
  const canMoveGroups = user?.role === "OWNER"
    || (!!user?.permissions?.products_edit && !!user?.permissions?.addons_edit);

  useEffect(() => { setCats(categories); }, [categories]);
  useEffect(() => { setProds(products); }, [products]);
  useEffect(() => {
    if (!sortHintStorageKey) {
      return;
    }
    try {
      setShowSortHint(window.localStorage.getItem(sortHintStorageKey) !== "true");
    } catch {
      setShowSortHint(false);
    }
  }, [sortHintStorageKey]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 120, tolerance: 8 } }),
  );

  const grouped = useMemo(
    () => cats.map(c => ({ ...c, items: prods.filter(p => p.category_id === c.id) })),
    [cats, prods],
  );

  const reload = async () => {
    const [c, p] = await Promise.all([api.get("/categories"), api.get("/products")]);
    setCats(c.data); setProds(p.data); onReload?.(c.data, p.data);
  };

  const findProd = (id) => prods.find(p => p.id === id);
  const findGroupOwner = (gid) => prods.find(p => (p.option_groups || []).some(g => g.id === gid));

  const save = async (fn) => {
    setBusy(true);
    try {
      await fn();
      await reload();
      toast.success("تم حفظ الترتيب", { position: "bottom-center" });
    } catch (e) {
      toast.error(formatErr(e));
      await reload();
    } finally {
      setBusy(false);
    }
  };

  const reorderCats = (activeId, overId) => {
    const from = cats.findIndex(c => c.id === activeId);
    const to = cats.findIndex(c => c.id === overId);
    if (from < 0 || to < 0 || from === to) return;
    const next = arrayMove(cats, from, to);
    setCats(next);
    save(() => api.post("/menu/reorder", { categories: next.map(c => c.id) }));
  };

  const reorderProducts = (catId, ids) => {
    setProds(prev => {
      const others = prev.filter(p => p.category_id !== catId);
      const map = new Map(prev.map(p => [p.id, p]));
      return [...others, ...ids.map((id, i) => ({ ...map.get(id), category_id: catId, sort: i }))];
    });
    save(() => api.post("/menu/reorder", { category_id: catId, product_ids: ids }));
  };

  const applyProductMove = (productId, targetCatId, index) => {
    const target = prods
      .filter(p => p.category_id === targetCatId && p.id !== productId)
      .map(p => p.id);
    target.splice(index === undefined || index < 0 ? target.length : index, 0, productId);
    reorderProducts(targetCatId, target);
  };

  const moveGroup = (payload) => save(() => api.post("/menu/option-groups/move", payload));

  const dismissSortHint = () => {
    if (!showSortHint) {
      return;
    }
    setShowSortHint(false);
    try {
      if (sortHintStorageKey) {
        window.localStorage.setItem(sortHintStorageKey, "true");
      }
    } catch {
      // The onboarding hint remains non-blocking when browser storage is unavailable.
    }
  };

  const handleDragStart = ({ active }) => {
    dismissSortHint();
    setActiveDrag({
      id: active.id,
      type: active.data.current?.type,
    });
  };

  const handleDragCancel = () => {
    setActiveDrag(null);
  };

  const collisionDetectionStrategy = (args) => {
    const activeType = args.active.data.current?.type;
    const seenTargets = new Set();

    return closestCorners(args).reduce((collisions, collision) => {
      const overData = collision.data?.droppableContainer?.data.current;
      let targetId = collision.id;

      if (activeType === "product") {
        if (overData?.type === "group") {
          targetId = findGroupOwner(collision.id)?.id || collision.id;
        } else if (overData?.type === "product-groups-drop") {
          targetId = overData.productId;
        } else if (overData?.type === "category") {
          targetId = `cat-drop-${collision.id}`;
        }
      }

      if (activeType === "category") {
        let targetCategoryId = null;
        if (overData?.type === "category") targetCategoryId = collision.id;
        if (overData?.type === "category-drop") targetCategoryId = overData.categoryId;
        if (overData?.type === "product") {
          targetCategoryId = findProd(collision.id)?.category_id;
        }
        if (overData?.type === "group") {
          targetCategoryId = findGroupOwner(collision.id)?.category_id;
        }
        if (overData?.type === "product-groups-drop") {
          targetCategoryId = findProd(overData.productId)?.category_id;
        }
        targetId = targetCategoryId || targetId;
      }

      if (!seenTargets.has(String(targetId))) {
        seenTargets.add(String(targetId));
        collisions.push({ ...collision, id: targetId });
      }

      return collisions;
    }, []);
  };

  const onDragEnd = ({ active, over }) => {
    if (!over || active.id === over.id) return;
    const type = active.data.current?.type;
    const overType = over.data.current?.type;

    if (type === "category" && overType === "category") return reorderCats(active.id, over.id);

    if (type === "product") {
      const p = findProd(active.id);
      if (!p) return;
      let overProdId = null, targetCat, index;
      if (overType === "product") overProdId = over.id;
      else if (overType === "group") overProdId = findGroupOwner(over.id)?.id;
      else if (overType === "product-groups-drop") overProdId = over.data.current.productId;
      else if (overType === "category-drop") targetCat = over.data.current.categoryId;
      else return;

      if (overProdId) {
        if (overProdId === p.id) return;
        const op = findProd(overProdId);
        if (!op) return;
        targetCat = op.category_id;
        const list = prods.filter(x => x.category_id === targetCat).map(x => x.id);
        index = list.indexOf(overProdId);
        if (targetCat === p.category_id) {
          const from = list.indexOf(active.id);
          return reorderProducts(targetCat, arrayMove(list, from, index));
        }
      } else if (targetCat === p.category_id) return;
      const catName = cats.find(c => c.id === targetCat)?.name || "";
      setPending({
        type: "product",
        message: `نقل «${p.name}» إلى قسم «${catName}»؟`,
        actions: [{ label: "نقل", run: () => applyProductMove(p.id, targetCat, index) }],
      });
      return;
    }

    if (type === "group") {
      if (!canMoveGroups) return;
      const srcProd = findGroupOwner(active.id);
      if (!srcProd) return;
      let targetProdId, index;
      if (overType === "group") {
        const tp = findGroupOwner(over.id);
        if (!tp) return;
        targetProdId = tp.id;
        index = (tp.option_groups || []).findIndex(g => g.id === over.id);
      } else if (overType === "product-groups-drop") {
        targetProdId = over.data.current.productId;
        index = undefined;
      } else return;

      if (targetProdId === srcProd.id) {
        return moveGroup({
          source_product_id: srcProd.id, target_product_id: srcProd.id,
          group_id: active.id, index, mode: "move",
        });
      }
      const gName = (srcProd.option_groups || []).find(g => g.id === active.id)?.name || "";
      const tName = findProd(targetProdId)?.name || "";
      const base = {
        source_product_id: srcProd.id,
        target_product_id: targetProdId,
        group_id: active.id,
        index,
      };
      setPending({
        type: "group",
        message: `مجموعة «${gName}» إلى صنف «${tName}»`,
        actions: [
          { label: "نقل", run: () => moveGroup({ ...base, mode: "move" }) },
          { label: "تكرار", run: () => moveGroup({ ...base, mode: "copy" }) },
        ],
      });
    }
  };

  const handleDragEnd = (event) => {
    setActiveDrag(null);
    onDragEnd(event);
  };

  let activeItem = null;
  if (activeDrag?.type === "category") {
    activeItem = cats.find(c => c.id === activeDrag.id);
  } else if (activeDrag?.type === "product") {
    activeItem = findProd(activeDrag.id);
  } else if (activeDrag?.type === "group") {
    activeItem = findGroupOwner(activeDrag.id)?.option_groups?.find(
      group => group.id === activeDrag.id,
    );
  }

  return (
    <div
      className="menu-order-editor-surface"
      data-testid="menu-order-editor"
      onClick={showSortHint ? dismissSortHint : undefined}
    >
      <div
        className={
          "sticky top-0 z-20 -mx-4 mb-4 flex items-center gap-2 border-b border-orange-200 "
          + "bg-orange-50/95 px-4 py-3 backdrop-blur"
        }
      >
        <div className="flex-1 text-sm font-bold text-orange-800">
          وضع ترتيب المنيو — اسحب من المقبض
        </div>
        <button
          onClick={onExit}
          disabled={busy}
          data-testid="done-order-btn"
          className={
            "pill brand-grad inline-flex items-center gap-1 px-4 py-2 font-bold text-white "
            + "disabled:opacity-60"
          }
        >
          <Check size={16}/>
          تم
        </button>
      </div>

      <DndContext
        sensors={sensors}
        collisionDetection={collisionDetectionStrategy}
        modifiers={[restrictToVerticalAxis]}
        measuring={{
          droppable: {
            strategy: MeasuringStrategy.Always,
          },
        }}
        onDragStart={handleDragStart}
        onDragCancel={handleDragCancel}
        onDragEnd={handleDragEnd}
      >
        <SortableContext
          items={cats.map(c => c.id)}
          strategy={verticalListSortingStrategy}
        >
          {grouped.map(c => (
            <Row key={c.id} id={c.id} data={{ type: "category" }} className="mb-4">
              {({ handleProps }) => (
                <section className="bg-white/70 rounded-3xl border border-orange-200 p-3">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="relative">
                      <button
                        {...handleProps}
                        data-testid={`drag-cat-${c.id}`}
                        className={
                          "grid h-9 w-9 place-items-center rounded-xl bg-orange-100 "
                          + "text-orange-700 cursor-grab "
                          + (showSortHint && c.id === cats[0]?.id
                            ? "menu-order-hint-handle"
                            : "")
                        }
                      >
                        <GripVertical size={18}/>
                      </button>
                      {showSortHint && c.id === cats[0]?.id && (
                        <div
                          className={
                            "absolute right-0 top-full z-30 mt-2 w-52 rounded-2xl border "
                            + "border-orange-200 bg-white p-3 shadow-lg rise-in"
                          }
                          role="tooltip"
                          data-testid="menu-sort-hint"
                          onClick={event => event.stopPropagation()}
                        >
                          <p className="text-xs font-bold leading-5 text-gray-700">
                            اسحب من هنا لإعادة الترتيب
                          </p>
                          <button
                            type="button"
                            onClick={dismissSortHint}
                            className="mt-2 text-xs font-extrabold text-orange-700"
                            data-testid="menu-sort-hint-dismiss"
                          >
                            فهمت
                          </button>
                        </div>
                      )}
                    </div>
                    <h2 className="font-extrabold text-lg">{c.name}</h2>
                  </div>
                  {!isDraggingCategory && (
                    <CategoryDrop categoryId={c.id}>
                      <SortableContext
                        items={c.items.map(p => p.id)}
                        strategy={verticalListSortingStrategy}
                      >
                        <div
                          className="space-y-2"
                          data-testid={`category-products-${c.id}`}
                        >
                          {c.items.length === 0 && (
                            <div
                              className={
                                "rounded-2xl border border-dashed border-orange-300 p-3 "
                                + "text-center text-xs text-gray-500"
                              }
                            >
                              أفلت صنفاً هنا
                            </div>
                          )}
                          {c.items.map(p => (
                            <Row key={p.id} id={p.id} data={{ type: "product" }}>
                              {({ handleProps }) => (
                                <div
                                  className="bg-white rounded-2xl border border-orange-100 p-3"
                                  data-testid={`order-item-${p.id}`}
                                >
                                  <div className="flex items-center gap-3">
                                    <button
                                      {...handleProps}
                                      data-testid={`drag-product-${p.id}`}
                                      className={
                                        "grid h-9 w-9 shrink-0 place-items-center rounded-xl "
                                        + "bg-orange-50 text-orange-700 cursor-grab"
                                      }
                                    >
                                      <GripVertical size={18}/>
                                    </button>
                                    <div
                                      className={
                                        "food-img h-10 w-10 shrink-0 overflow-hidden "
                                        + "rounded-xl"
                                      }
                                    >
                                      {p.image_url
                                        ? <img
                                          src={absoluteUrl(p.image_url)}
                                          alt=""
                                          className="w-full h-full object-cover"
                                        />
                                        : (
                                          <div
                                            className={
                                              "grid h-full w-full place-items-center "
                                              + "text-lg"
                                            }
                                          >
                                            🍽️
                                          </div>
                                        )}
                                    </div>
                                    <div className="font-bold truncate flex-1">{p.name}</div>
                                  </div>
                                  {!isDraggingProduct && (
                                    <ProductGroupsDrop productId={p.id}>
                                      <SortableContext
                                        items={(p.option_groups || []).map(g => g.id)}
                                        strategy={verticalListSortingStrategy}
                                      >
                                        <div className="mt-2 ms-11 space-y-1.5">
                                          {canMoveGroups && (p.option_groups || []).length === 0 && (
                                            <div
                                              className={
                                                "rounded-xl border border-dashed border-gray-200 "
                                                + "p-2 text-center text-[11px] text-gray-400"
                                              }
                                            >
                                              أفلت مجموعة خيارات هنا
                                            </div>
                                          )}
                                          {(p.option_groups || []).map(g => (
                                            <Row key={g.id} id={g.id} data={{ type: "group" }}>
                                              {({ handleProps }) => (
                                                <div
                                                  className={
                                                    "flex items-center gap-2 rounded-xl border "
                                                    + "border-gray-200 bg-gray-50 px-2 py-1.5"
                                                  }
                                                  data-testid={`order-group-${g.id}`}
                                                >
                                                  {canMoveGroups && (
                                                    <button
                                                      {...handleProps}
                                                      data-testid={`drag-group-${g.id}`}
                                                      className={
                                                        "grid h-7 w-7 shrink-0 place-items-center "
                                                        + "rounded-lg bg-white text-gray-500 "
                                                        + "cursor-grab"
                                                      }
                                                    >
                                                      <GripVertical size={14} />
                                                    </button>
                                                  )}
                                                  <div
                                                    className={
                                                      "flex min-w-0 flex-1 flex-wrap items-center "
                                                      + "gap-1.5"
                                                    }
                                                  >
                                                    <span className="text-xs font-bold break-words">
                                                      {g.name}
                                                    </span>
                                                    <GroupOptionBadges group={g} />
                                                  </div>
                                                </div>
                                              )}
                                            </Row>
                                          ))}
                                        </div>
                                      </SortableContext>
                                    </ProductGroupsDrop>
                                  )}
                                </div>
                              )}
                            </Row>
                          ))}
                        </div>
                      </SortableContext>
                    </CategoryDrop>
                  )}
                </section>
              )}
            </Row>
          ))}
        </SortableContext>
        <DragOverlay
          dropAnimation={{
            duration: 160,
            easing: "cubic-bezier(0.2, 0, 0, 1)",
          }}
        >
          {activeItem && <DragPreview item={activeItem} type={activeDrag?.type} />}
        </DragOverlay>
      </DndContext>

      {pending && (
        <div
          className="fixed inset-0 z-50 grid place-items-end bg-black/50 p-4 sm:place-items-center"
          data-testid="order-confirm"
        >
          <div className="w-full max-w-sm bg-white rounded-3xl p-5">
            <div className="font-extrabold text-base mb-4">{pending.message}</div>
            <div className="space-y-2">
              {pending.actions.map((a, i) => (
                <button
                  key={a.label}
                  data-testid={`confirm-action-${i}`}
                  onClick={() => {
                    const run = a.run;
                    setPending(null);
                    run();
                  }}
                  className={
                    `w-full pill py-3 font-bold ${i === 0
                      ? "brand-grad text-white"
                      : "bg-orange-50 text-orange-700 border border-orange-200"}`
                  }
                >
                  {a.label}
                </button>
              ))}
              <button
                onClick={() => setPending(null)}
                data-testid="confirm-cancel"
                className="w-full pill bg-gray-100 py-3 font-bold text-gray-600"
              >
                إلغاء
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function GroupOptionBadges({ group }) {
  const options = (group.options || []).filter(option => option.name?.trim());

  if (options.length === 0) {
    return null;
  }

  return (
    <div
      className="flex max-w-full flex-wrap items-center gap-1"
      data-testid={`order-group-options-${group.id}`}
    >
      {options.map((option, index) => (
        <span
          key={option.id || `${group.id}-${index}`}
          className={
            "max-w-full break-words rounded-full border border-orange-200 bg-orange-50 "
            + "px-2 py-0.5 text-[11px] font-medium text-orange-800"
          }
          data-testid={`order-group-option-${group.id}-${option.id || index}`}
        >
          {option.name}
        </span>
      ))}
    </div>
  );
}

function DragPreview({ item, type }) {
  if (type === "category") {
    return (
      <div
        className={
          "menu-order-drag-overlay min-w-56 rounded-3xl border border-orange-300 bg-white p-3"
        }
        data-testid="category-drag-overlay"
      >
        <div className="flex items-center gap-2">
          <span
            className="grid h-9 w-9 place-items-center rounded-xl bg-orange-100 text-orange-700"
          >
            <GripVertical size={18} />
          </span>
          <span className="font-extrabold text-lg">{item.name}</span>
        </div>
      </div>
    );
  }

  if (type === "product") {
    return (
      <div
        className={
          "menu-order-drag-overlay min-w-64 rounded-2xl border border-orange-300 bg-white p-3"
        }
        data-testid="product-drag-overlay"
      >
        <div className="flex items-center gap-3">
          <span
            className="grid h-9 w-9 place-items-center rounded-xl bg-orange-50 text-orange-700"
          >
            <GripVertical size={18} />
          </span>
          <span className="food-img h-10 w-10 shrink-0 overflow-hidden rounded-xl">
            {item.image_url ? (
              <img
                src={absoluteUrl(item.image_url)}
                alt=""
                className="h-full w-full object-cover"
              />
            ) : (
              <span className="grid h-full w-full place-items-center text-lg">🍽️</span>
            )}
          </span>
          <span className="font-bold truncate">{item.name}</span>
        </div>
      </div>
    );
  }

  return (
    <div
      className={
        "menu-order-drag-overlay min-w-48 rounded-xl border border-orange-300 bg-gray-50 "
        + "px-2 py-1.5"
      }
      data-testid="group-drag-overlay"
    >
      <div className="flex items-center gap-2">
        <span className="grid h-7 w-7 place-items-center rounded-lg bg-white text-gray-500">
          <GripVertical size={14} />
        </span>
        <span className="text-xs font-bold truncate">{item.name}</span>
      </div>
    </div>
  );
}

function CategoryDrop({ categoryId, children }) {
  const { setNodeRef, isOver } = useDroppable({
    id: `cat-drop-${categoryId}`,
    data: { type: "category-drop", categoryId },
  });

  return (
    <div
      ref={setNodeRef}
      className={isOver ? "menu-order-drop-active" : ""}
      data-testid={`category-drop-${categoryId}`}
    >
      {children}
    </div>
  );
}

function ProductGroupsDrop({ productId, children }) {
  const { setNodeRef, isOver } = useDroppable({
    id: `groups-drop-${productId}`,
    data: { type: "product-groups-drop", productId },
  });

  return (
    <div
      ref={setNodeRef}
      className={isOver ? "menu-order-drop-active" : ""}
      data-testid={`product-groups-drop-${productId}`}
    >
      {children}
    </div>
  );
}
